import { useTheme } from '@/src/context/ThemeContext';
import { UseCaseLandingConfig, UseCaseLandingScreen } from '@/src/presentation/screens/UseCaseLandingScreen';
import Head from 'expo-router/head';
import { useRouter } from 'expo-router';

const PAGE_URL = 'https://tournaly.app/tournament-bracket-maker';
const OG_IMAGE = 'https://tournaly.app/og-image.png';

const CONFIG: UseCaseLandingConfig = {
  badge: 'Free Online Tool',
  badgeEs: 'Herramienta Gratuita Online',
  badgeIcon: 'trophy-outline',
  title: 'The Free Tournament',
  titleEs: 'El Creador de Brackets',
  titleAccent: 'Bracket Maker',
  titleAccentEs: 'para Torneos Gratis',
  subtitle:
    'Create, manage and share brackets for any format — single elimination, double elimination, round robin, Swiss and more. Free for up to 16 players.',
  subtitleEs:
    'Crea, gestiona y comparte brackets para cualquier formato — eliminación simple, doble, round robin, swiss y más. Gratis hasta 16 jugadores.',
  steps: [
    {
      title: 'Create your tournament',
      titleEs: 'Crea tu torneo',
      desc: 'Choose a format, set a name, pick your visibility and you are ready to go.',
      descEs: 'Elige un formato, ponle nombre, decide la visibilidad y listo.',
    },
    {
      title: 'Add participants',
      titleEs: 'Añade participantes',
      desc: 'Add players manually, search by email or share your invite code so they join themselves.',
      descEs: 'Añade jugadores manualmente, búscalos por email o comparte tu código de invitación.',
    },
    {
      title: 'Generate & play',
      titleEs: 'Genera y juega',
      desc: 'The bracket auto-generates in seconds. Update results as matches are played — standings update instantly.',
      descEs: 'El bracket se genera automáticamente. Actualiza resultados al instante.',
    },
  ],
  features: [
    {
      icon: 'git-branch-outline',
      color: '#FF6B35',
      title: '6 Tournament Formats',
      titleEs: '6 Formatos de Torneo',
      desc: 'Single & double elimination, round robin, Swiss, King of the Hill and triple elimination.',
      descEs: 'Eliminación simple y doble, round robin, Swiss, Rey de la Colina y triple eliminación.',
    },
    {
      icon: 'flash-outline',
      color: '#4ECDC4',
      title: 'Auto-Generated Brackets',
      titleEs: 'Brackets Automáticos',
      desc: 'Add participants and hit generate — seeded brackets are built in one tap.',
      descEs: 'Añade participantes y pulsa generar — los brackets con cabezas de serie se crean al instante.',
    },
    {
      icon: 'bar-chart-outline',
      color: '#45B7D1',
      title: 'Live Standings',
      titleEs: 'Clasificación en Vivo',
      desc: 'Real-time leaderboards updated automatically with every result.',
      descEs: 'Clasificaciones en tiempo real actualizadas automáticamente con cada resultado.',
    },
    {
      icon: 'share-social-outline',
      color: '#06B6D4',
      title: 'Export & Share',
      titleEs: 'Exportar y Compartir',
      desc: 'Export as PNG or PDF. Generate QR codes and shareable links.',
      descEs: 'Exporta como PNG o PDF. Genera códigos QR y enlaces compartibles.',
    },
    {
      icon: 'qr-code-outline',
      color: '#10B981',
      title: 'Invite Codes',
      titleEs: 'Códigos de Invitación',
      desc: 'Share an 8-character code — players join instantly without an account.',
      descEs: 'Comparte un código de 8 caracteres — los jugadores se unen al instante sin cuenta.',
    },
    {
      icon: 'phone-portrait-outline',
      color: '#A78BFA',
      title: 'iOS & Android Apps',
      titleEs: 'Apps para iOS y Android',
      desc: 'Manage your tournaments from anywhere with the free mobile app.',
      descEs: 'Gestiona tus torneos desde cualquier lugar con la app móvil gratuita.',
    },
  ],
  relatedBlogSlug: '2026-03-06-tournament-bracket-maker',
  relatedBlogTitle: 'How to Create a Tournament Bracket — Complete Guide',
  relatedBlogTitleEs: 'Cómo Crear un Bracket de Torneo — Guía Completa',
  relatedBlogSummary: 'Step-by-step guide to setting up your first tournament bracket online, choosing the right format and managing results.',
  relatedBlogSummaryEs: 'Guía paso a paso para configurar tu primer bracket de torneo online, elegir el formato adecuado y gestionar resultados.',
};

const title = 'Free Tournament Bracket Maker Online — Tournaly';
const description =
  'Create and manage tournament brackets online for free. Single elimination, double elimination, round robin, Swiss and more. Up to 16 players free.';
const titleEs = 'Creador de Brackets para Torneos Online Gratis — Tournaly';
const descriptionEs =
  'Crea y gestiona brackets de torneo online gratis. Eliminación simple, doble, round robin, Swiss y más. Hasta 16 jugadores gratis.';

export default function TournamentBracketMakerPage() {
  const { colors, isDark, toggleTheme } = useTheme();
  const router = useRouter();

  const jsonLd = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Tournaly — Tournament Bracket Maker',
    applicationCategory: 'SportsApplication',
    operatingSystem: 'Web, iOS, Android',
    url: PAGE_URL,
    description,
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    publisher: { '@type': 'Organization', name: 'Tournaly', url: 'https://tournaly.app' },
  });

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={PAGE_URL} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Tournaly" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={OG_IMAGE} />
        <meta name="keywords" content="tournament bracket maker, free bracket maker, online bracket maker, tournament maker, bracket generator" />
        <script type="application/ld+json">{jsonLd}</script>
      </Head>
      <UseCaseLandingScreen
        config={CONFIG}
        isDark={isDark}
        onToggleTheme={toggleTheme}
        onGetStarted={() => router.push('/auth/register' as any)}
        onSignIn={() => router.push('/auth/login' as any)}
        onBlogPostPress={(slug) => {
          if (slug === 'blog') router.push('/blog' as any);
          else if (slug === 'privacy') router.push('/privacy' as any);
          else if (slug === 'terms') router.push('/terms' as any);
          else router.push(`/blog/${slug}` as any);
        }}
        colors={colors}
      />
    </>
  );
}
